export const RESEARCH_REPO = {
  canonicalPath: '/research/municipal-transportation-emissions',
  canonicalUrl: 'https://climateplans.org/research/municipal-transportation-emissions/',
  aliasPath: '/transportation-paper',
  title:
    'Harmonizing Municipal Transportation Emissions: Technology, Travel, and Policy-Regime Sensitivity Across California Cities',
  subtitle: '',
  authors: ['Christopher M. Jones', 'Kaihui Song', 'Daniel M. Kammen'],
  journal: 'Carbon Footprints',
  statusBadge: 'Invited manuscript prepared for Carbon Footprints',
  specialIssue:
    'Urban & Rural Carbon Footprints: Built Environments, Mobility, and Lifestyle Transitions',
  year: 2026,
  release: 'v0.9.0',
  releaseLabel: 'Prepublication manuscript-support release',
  lastUpdated: '2026-07-09',
  funding:
    'This work was supported by the University of California California Climate Action Seed Grant R02CP7580, “Climate Action Planning Tools: Empowering Equitable Transitions for California Communities.”',
} as const;

export const STUDY_SUMMARY =
  'This study develops a harmonized annual on-road transportation greenhouse gas inventory for California’s 482 incorporated municipalities and 58 counties. It compares harmonized estimates with published Climate Action Plan inventories, decomposes 2015–2023 light-duty vehicle emissions changes, classifies heterogeneous municipal pathways, and evaluates the sensitivity of prospective emissions to alternative EMFAC model releases and represented policy regimes.';

export const CONTRIBUTIONS = [
  {
    title: 'Harmonized municipal accounting',
    text: 'Consistent statewide boundaries and methods across 482 cities and 58 counties.',
  },
  {
    title: 'Exact historical decomposition',
    text: 'Formal three-factor LMDI with exact closure on the 449-municipality LDV panel.',
  },
  {
    title: 'Municipal pathway heterogeneity',
    text: 'Four-quadrant dominant-factor classification across heterogeneous local trajectories.',
  },
  {
    title: 'EMFAC policy-regime sensitivity',
    text: 'Fixed-activity comparison of official CARB model releases and represented assumptions.',
  },
] as const;

export const KEY_FINDINGS = [
  { label: 'Incorporated municipalities', value: '482' },
  { label: 'Counties', value: '58' },
  { label: 'Matched CAP inventories', value: '53' },
  { label: 'Computable LMDI panel', value: '449 municipalities' },
  { label: '2015–2023 LDV net change', value: '−11.086 MMT CO₂e' },
  { label: 'Population effect', value: '+0.895 MMT' },
  { label: 'VMT-per-capita effect', value: '+2.490 MMT' },
  { label: 'Composite emission-factor effect', value: '−14.471 MMT' },
  { label: 'Technology-led reductions', value: '304 municipalities' },
  { label: 'Municipalities with increasing emissions', value: '84' },
  { label: 'Fixed-activity 2050 EMFAC range', value: '10.5–104.0 MMT CO₂e' },
] as const;

export const REPRO_STATUS = [
  ['LMDI decomposition', 'Yes', 'Derived analytical inputs included'],
  ['Pathway classification', 'Yes', 'Derived analytical inputs included'],
  ['EMFAC sensitivity', 'Yes', 'Public CARB model data'],
  ['Main figures', 'Yes', 'Locked plotting extracts included'],
  ['Direct 2023–2025 municipal VMT', 'Partial', 'Replica license required'],
  ['Complete raw inventory pipeline', 'Partial', 'Replica license required'],
] as const;

export const CITATION_PLAIN = `Jones CM, Song K, Kammen DM. Harmonizing Municipal Transportation Emissions: Technology, Travel, and Policy-Regime Sensitivity Across California Cities. Carbon Footprints. 2026. [DOI pending].`;

export const CITATION_REPO = `Jones CM, Song K. ClimatePlans municipal transportation emissions analysis: data, code, and reproducibility materials. Version 0.9.0. 2026. [Repository DOI pending].`;

export const PUBLIC_AGENCIES = [
  { name: 'Caltrans', url: 'https://dot.ca.gov/' },
  { name: 'California Air Resources Board (EMFAC)', url: 'https://ww2.arb.ca.gov/emfac' },
  { name: 'U.S. Census Bureau', url: 'https://www.census.gov/' },
  { name: 'California Department of Finance', url: 'https://dof.ca.gov/' },
] as const;

export type ManifestFile = {
  title: string;
  filename: string;
  type: string;
  description: string;
  size_bytes: number;
  sha256: string;
  version: string;
  status: string;
  license: string;
  source: string;
  download_path: string;
};

export type ManifestDoc = {
  release: string;
  generated: string;
  files: ManifestFile[];
  pending: { title: string; type: string; status: string }[];
};
